/**
 * =============================================================================
 * SPEQ55 RFP Pro - Main Application Component
 * =============================================================================
 *
 * This is the root component of the React application. It:
 * 1. Defines the application's route structure
 * 2. Wraps protected routes with authentication
 * 3. Handles top-level error boundaries
 *
 * Route Structure:
 * ----------------
 * PUBLIC MARKETING ROUTES (no authentication required):
 * /                  - Landing page (Home)
 * /about             - About page
 * /pricing           - Pricing page
 * /contact           - Contact page
 *
 * PUBLIC AUTH ROUTES (no authentication required):
 * /login             - Login page
 * /forgot-password   - Request password reset
 * /reset-password    - Complete password reset
 *
 * PROTECTED ROUTES (authentication required):
 * /app               - Dashboard (landing page after login)
 * /app/rfps          - RFP listing page
 * /app/rfps/:id      - RFP detail page
 * /app/proposals     - Proposal listing page
 * /app/settings      - User settings
 *
 * ADMIN ROUTES (admin_sales or super_admin required):
 * /admin             - Admin dashboard
 * /admin/users       - User management
 *
 * CATCH-ALL:
 * *                  - 404 Not Found
 *
 * @module App
 * @author SPEQ55
 */

import { Routes, Route } from 'react-router-dom';

// =============================================================================
// LAYOUT COMPONENTS
// =============================================================================

// Public layout with marketing navigation
import PublicLayout from './components/layout/PublicLayout';

// Main layout with header and sidebar for authenticated users
import Layout from './components/layout/Layout';

// =============================================================================
// ROUTE PROTECTION
// =============================================================================

// Protected route wrapper for authenticated-only pages
import ProtectedRoute from './components/auth/ProtectedRoute';

// =============================================================================
// PUBLIC MARKETING PAGE COMPONENTS
// =============================================================================

import Home from './pages/public/Home';
import About from './pages/public/About';
import Pricing from './pages/public/Pricing';
import Contact from './pages/public/Contact';

// =============================================================================
// AUTH PAGE COMPONENTS
// =============================================================================

// Public authentication pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// =============================================================================
// PAGE COMPONENTS
// =============================================================================

// Main application pages
import Dashboard from './pages/Dashboard';
import Feedback from './pages/Feedback';
import NotFound from './pages/NotFound';

// RFP pages
import RFPList from './pages/rfps/RFPList';
import RFPDetail from './pages/rfps/RFPDetail';
import RFPCreate from './pages/rfps/RFPCreate';
import RFPEdit from './pages/rfps/RFPEdit';
import RFPAnalyze from './pages/rfps/RFPAnalyze';
import RFPAnalysisResults from './pages/rfps/RFPAnalysisResults';

// Parameter set pages
import ParameterSetsList from './pages/parameter-sets/ParameterSetsList';
import ParameterSetForm from './pages/parameter-sets/ParameterSetForm';

// Admin pages
import UsageManagement from './pages/admin/UsageManagement';
import CostMonitoring from './pages/admin/CostMonitoring';
import PricingManagement from './pages/admin/PricingManagement';
import ModelSelection from './pages/admin/ModelSelection';
import UserManagement from './pages/admin/UserManagement';
import OrganizationManagement from './pages/admin/OrganizationManagement';
import AdminRedirect from './components/admin/AdminRedirect';

// =============================================================================
// APP COMPONENT
// =============================================================================

/**
 * Main application component.
 *
 * Defines the route structure with three main sections:
 * 1. Public marketing routes (home, about, pricing, contact) - PublicLayout wrapper
 * 2. Public auth routes (login, password reset) - no Layout wrapper
 * 3. Protected routes (dashboard, etc.) - wrapped in Layout + ProtectedRoute
 * 4. Catch-all 404 route
 *
 * @returns {JSX.Element} The rendered application
 */
function App() {
  return (
    <Routes>
      {/*
        =========================================================================
        PUBLIC MARKETING ROUTES
        =========================================================================
        These routes are accessible without authentication.
        They use the PublicLayout with marketing navigation.
      */}
      <Route element={<PublicLayout />}>
        {/*
          Landing Page (Home)
          --------------------
          Main marketing landing page with hero section.
        */}
        <Route path="/" element={<Home />} />

        {/*
          About Page
          ----------
          Company information and mission.
        */}
        <Route path="/about" element={<About />} />

        {/*
          Pricing Page
          ------------
          Subscription plans and pricing.
        */}
        <Route path="/pricing" element={<Pricing />} />

        {/*
          Contact Page
          ------------
          Contact form and company information.
        */}
        <Route path="/contact" element={<Contact />} />
      </Route>

      {/*
        =========================================================================
        PUBLIC AUTH ROUTES
        =========================================================================
        These routes are accessible without authentication.
        They use standalone page layouts without the main Layout.
      */}

      {/*
        Login Page
        ----------
        Main authentication entry point.
        Redirects to dashboard if already authenticated.
      */}
      <Route path="/login" element={<Login />} />

      {/*
        Forgot Password Page
        --------------------
        Request a password reset email.
        Always shows success to prevent email enumeration.
      */}
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/*
        Reset Password Page
        -------------------
        Complete password reset with token from email.
        URL format: /reset-password?token=<reset-token>
      */}
      <Route path="/reset-password" element={<ResetPassword />} />

      {/*
        =========================================================================
        PROTECTED ROUTES
        =========================================================================
        These routes require authentication.
        Unauthenticated users are redirected to /login.
        All routes use the main Layout with header and sidebar.
      */}

      {/*
        Main Layout Route
        -----------------
        All routes nested under this use the main layout with
        header, sidebar, and main content area.
        The ProtectedRoute wrapper ensures authentication.
      */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/*
          Dashboard / Home
          ----------------
          Index route renders when path exactly matches parent ("/app")
          This is the main landing page after login.
        */}
        <Route index element={<Dashboard />} />

        {/*
          RFP Routes
          ----------
          RFP listing, detail, create, and edit pages.
        */}
        <Route path="rfps" element={<RFPList />} />
        <Route path="rfps/create" element={<RFPCreate />} />
        <Route path="rfps/:id" element={<RFPDetail />} />
        <Route path="rfps/:id/edit" element={<RFPEdit />} />
        <Route path="rfps/:id/analyze" element={<RFPAnalyze />} />
        <Route path="rfps/:id/analysis/:analysisId" element={<RFPAnalysisResults />} />

        {/*
          Parameter Set Routes
          --------------------
          Reusable keyword + technical-spec collections users curate once
          and then pick from when starting an RFP analysis (RFPAnalyze pulls
          from them via the dropdown). ParameterSetForm is reused for both
          create and edit — the presence of :id switches the mode.

          Access control is enforced server-side; every authenticated user
          can navigate to these pages. The visibility rules (org_default /
          shared / private) are enforced when reading/writing via the
          /api/parameter-sets endpoints.
        */}
        <Route path="parameter-sets" element={<ParameterSetsList />} />
        <Route path="parameter-sets/new" element={<ParameterSetForm />} />
        <Route path="parameter-sets/:id/edit" element={<ParameterSetForm />} />

        {/*
          Feedback Route
          --------------
          User feedback and issue reporting page.
        */}
        <Route path="feedback" element={<Feedback />} />

        {/*
          Proposal Routes
          ---------------
          Proposal listing and detail pages.
          (Placeholder for future implementation)
        */}
        {/* <Route path="proposals" element={<ProposalList />} /> */}
        {/* <Route path="proposals/:id" element={<ProposalDetail />} /> */}

        {/*
          Settings Route
          --------------
          User settings and preferences.
          (Placeholder for future implementation)
        */}
        {/* <Route path="settings" element={<Settings />} /> */}
      </Route>

      {/*
        =========================================================================
        LEGACY REDIRECT
        =========================================================================
        Redirect old root dashboard to new /app path.
        This ensures existing bookmarks and links continue to work.
      */}

      {/*
        =========================================================================
        ADMIN ROUTES
        =========================================================================
        These routes require admin privileges (super_admin, admin_sales, or org_admin).
        Admin routes are nested under /app/admin to use the same layout.
      */}
      <Route
        path="/app/admin"
        element={
          <ProtectedRoute requiredRoles={['super_admin', 'admin_sales', 'org_admin']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/*
          Admin Index Route
          -----------------
          Redirects to appropriate admin page based on user role:
          - super_admin/admin_sales -> User Management
          - org_admin -> Usage Management
        */}
        <Route index element={<AdminRedirect />} />

        {/*
          User Management
          ---------------
          Manage user accounts and permissions.
          - super_admin and admin_sales can manage all users
          - View, edit, deactivate, and delete users
        */}
        <Route path="users" element={<UserManagement />} />

        {/*
          Organization Management
          -----------------------
          Create and list organizations.
          - super_admin and admin_sales only (enforced in-page)
        */}
        <Route path="organizations" element={<OrganizationManagement />} />

        {/*
          Usage Management
          ----------------
          View and manage user usage limits.
          - super_admin and admin_sales can modify limits
          - org_admin can view usage for their organization (read-only)
        */}
        <Route path="usage" element={<UsageManagement />} />

        {/*
          Cost Monitoring
          ---------------
          View API usage costs and billing guardrails.
          - Real-time budget tracking
          - Daily cost breakdown
          - Per-user cost analysis
        */}
        <Route path="costs" element={<CostMonitoring />} />

        {/*
          Pricing Management
          ------------------
          Manage API pricing configuration (super_admin only).
          - Update pricing without code changes
          - View pricing history
          - Audit trail of all changes
        */}
        <Route path="pricing" element={<PricingManagement />} />

        {/*
          Model Selection
          ---------------
          Select which Gemini models fill the default/extended slots.
          - super_admin and admin_sales can change model configuration
        */}
        <Route path="models" element={<ModelSelection />} />
      </Route>

      {/*
        =========================================================================
        404 NOT FOUND ROUTE
        =========================================================================
        Catches all unmatched routes and displays a 404 page.
        Uses the wildcard "*" pattern to match anything not caught above.
        No authentication required - shows generic not found page.
      */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default App;
